import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  DialogTrigger,
} from "@fluentui/react-components";
import { DismissRegular } from "@fluentui/react-icons";
import { VaultMeetingsFilterBand } from "apps/project-iq/components/VaultMeetingsFilterBand/VaultMeetingsFilterBand";
import { VaultMeetingsToolbar } from "apps/project-iq/components/VaultMeetingsToolbar/VaultMeetingsToolbar";
import { VaultMeetingsWeekGrid } from "apps/project-iq/components/VaultMeetingsWeekGrid/VaultMeetingsWeekGrid";
import { useId, useMemo, useState } from "react";
import {
  DAYS_PER_WEEK,
  EMPTY_VAULT_MEETING_FILTERS,
} from "./VaultMeetingsDialog.constants";
import { useVaultMeetingsDialogStyles } from "./VaultMeetingsDialog.styles";
import {
  IVaultMeetingFilters,
  IVaultMeetingsDialogProps,
} from "./VaultMeetingsDialog.types";
import {
  addDays,
  countActiveFilters,
  countMeetings,
  filterMeetings,
  formatMeetingSummary,
  getMeetingsForWeek,
  getWeekRangeLabel,
  getWeekStart,
} from "./VaultMeetingsDialog.utils";

export const VaultMeetingsDialog = ({
  meetings,
  today,
  onClose,
}: IVaultMeetingsDialogProps): JSX.Element => {
  const styles = useVaultMeetingsDialogStyles();
  const filterBandId = useId();
  const [weekOffset, setWeekOffset] = useState<number>(0);
  const [isFilterBandOpen, setIsFilterBandOpen] = useState<boolean>(false);
  const [filters, setFilters] = useState<IVaultMeetingFilters>(
    EMPTY_VAULT_MEETING_FILTERS,
  );

  const weekStart = useMemo(
    () => addDays(getWeekStart(today), weekOffset * DAYS_PER_WEEK),
    [today, weekOffset],
  );
  const weekMeetings = useMemo(
    () => getMeetingsForWeek(meetings, weekStart),
    [meetings, weekStart],
  );
  const shownMeetings = useMemo(
    () => filterMeetings(weekMeetings, filters),
    [weekMeetings, filters],
  );

  return (
    <Dialog
      open
      onOpenChange={(_, data) => {
        if (!data.open) onClose();
      }}
    >
      <DialogSurface className={styles.surface}>
        <DialogBody className={styles.body}>
          <div className={styles.titleBar}>
            <DialogTitle className={styles.title}>Meetings</DialogTitle>
            <DialogTrigger action="close">
              <Button
                appearance="subtle"
                className={styles.closeButton}
                aria-label="Close meetings"
                icon={<DismissRegular aria-hidden="true" />}
              />
            </DialogTrigger>
          </div>
          <DialogContent className={styles.content}>
            <VaultMeetingsToolbar
              weekRangeLabel={getWeekRangeLabel(weekStart)}
              summaryText={formatMeetingSummary(countMeetings(shownMeetings))}
              isCurrentWeek={weekOffset === 0}
              activeFilterCount={countActiveFilters(filters)}
              isFilterBandOpen={isFilterBandOpen}
              filterBandId={filterBandId}
              onPreviousWeek={() => setWeekOffset((offset) => offset - 1)}
              onNextWeek={() => setWeekOffset((offset) => offset + 1)}
              onToday={() => setWeekOffset(0)}
              onToggleFilters={() => setIsFilterBandOpen((isOpen) => !isOpen)}
            />
            {isFilterBandOpen && (
              <VaultMeetingsFilterBand
                id={filterBandId}
                filters={filters}
                shownCount={shownMeetings.length}
                weekCount={weekMeetings.length}
                onChange={setFilters}
                onClear={() => setFilters(EMPTY_VAULT_MEETING_FILTERS)}
              />
            )}
            <VaultMeetingsWeekGrid
              weekStart={weekStart}
              today={today}
              meetings={shownMeetings}
            />
          </DialogContent>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};
